import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  KeyboardEvent,
} from 'react';
import { Box, OutlinedInputProps, Typography } from '@mui/material';
import { GhostButton } from '@ui/GhostButton';
import NodonTextField from '@ui/NodonTextField';
import { useBooleanState } from '@client/hooks/use-utils.hooks';
import { makeStyles } from 'tss-react/mui';
import { useCommentsStore } from '@client/store/comment/comment.store';
import { COMMENT_MAX_LENGTH, NEW_COMMENT_ID_PREFIX } from '@shared/constants';
import {
  getDraft,
  setDraft,
  useStoreDraftComment,
  useFocusCommentInput,
} from '@client/store/comment/comments.hook';

interface CommentInputProps {
  id: string;
  message: string;
  placeholder?: string;
  onSave: (message: string) => void;
}

export const CommentInput: React.FC<CommentInputProps> = ({
  id,
  message,
  onSave,
  placeholder = 'Add a comment...',
}) => {
  const { classes } = useStyles();

  const { error, setEditingId, editingId } = useCommentsStore(
    ({ error, setEditingId, editingId }) => ({
      error,
      setEditingId,
      editingId,
    }),
  );

  const [buttonsVisible, showButtons, hideButtons] = useBooleanState();
  const [inputValue, setInputValue] = useState(getDraft(id) || message);
  const [localError, setLocalError] = useState('');

  useStoreDraftComment(id, message, inputValue);
  const inputRef = useFocusCommentInput(id, message);

  const isNewComment = id.startsWith(NEW_COMMENT_ID_PREFIX);
  const isEditing = editingId === id;

  const handleSave = useCallback(() => {
    onSave(inputValue);
    setDraft(id, undefined);
  }, [id, inputValue, onSave]);

  const handleCancel = useCallback(() => {
    setEditingId(undefined);
    setDraft(id, undefined);
  }, [id, setEditingId]);

  const handleInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { key, shiftKey, currentTarget } = event;

      if (key === 'Escape') {
        currentTarget.blur();
        handleCancel();
      }
      if (key === 'Enter' && shiftKey === false) {
        event.preventDefault();

        if (!inputValue.trim() || inputValue.length > COMMENT_MAX_LENGTH) {
          return;
        }
        currentTarget.blur();
        handleSave();
      }
    },
    [handleCancel, handleSave, inputValue],
  );
  const handleBlur = useCallback(() => {
    if (inputValue === '') setEditingId(undefined);
  }, [inputValue, setEditingId]);

  const handleInputChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      if (buttonsVisible) {
        setInputValue(value);
        setLocalError(
          value.length >= COMMENT_MAX_LENGTH ? 'Comment is too long' : '',
        );
      }
    },
    [buttonsVisible],
  );

  const inputProps = useMemo<OutlinedInputProps>(
    () => ({
      classes: {
        root: classes.outlinedInput,
        notchedOutline: classes.fieldsetBorder,
        disabled: classes.disabled,
      },
      inputProps: {
        maxLength: COMMENT_MAX_LENGTH,
        onKeyDown: handleInputKeyDown,
        onBlur: handleBlur,
      },
    }),
    [
      classes.disabled,
      classes.fieldsetBorder,
      classes.outlinedInput,
      handleInputKeyDown,
      handleBlur,
    ],
  );

  const handleInputClick = useCallback(() => {
    if (isNewComment && !isEditing) {
      setEditingId(id);
    }
  }, [id, isEditing, isNewComment, setEditingId]);

  useEffect(() => {
    if (error?.id === id) {
      setLocalError(error.message);
    }
  }, [error, id]);

  useEffect(() => {
    if (isEditing) {
      setInputValue(getDraft(id) || message);
      showButtons();
    }
    if (!isEditing) {
      setInputValue(message);
      setLocalError('');
      hideButtons();
    }
  }, [hideButtons, id, isEditing, message, showButtons]);

  return (
    <Box
      sx={{
        width: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <NodonTextField
        multiline
        variant="outlined"
        size="small"
        value={inputValue}
        placeholder={message ? '' : placeholder}
        disabled={!isNewComment && !isEditing}
        inputRef={inputRef}
        InputProps={inputProps}
        onChange={handleInputChange}
        onFocus={handleInputClick}
        onClick={handleInputClick}
      />

      <Box
        sx={{
          display: buttonsVisible ? 'flex' : 'none',
          gap: 2,
          pt: 5,
          pb: 4,
        }}
      >
        <GhostButton onClick={handleCancel}>Cancel</GhostButton>
        <GhostButton
          onClick={handleSave}
          disabled={
            !inputValue ||
            inputValue === message ||
            inputValue.length > COMMENT_MAX_LENGTH
          }
        >
          Save
        </GhostButton>
      </Box>
      {localError && (
        <Typography
          variant="caption"
          color="error"
          sx={{
            pt: 2,
            textAlign: 'center',
          }}
        >
          {localError}
        </Typography>
      )}
    </Box>
  );
};

const useStyles = makeStyles()(({ spacing }) => ({
  fieldsetBorder: { border: 0 },
  disabled: {
    '&.Mui-disabled': {
      WebkitTextFillColor: 'rgb(0 0 0 / 80%)',
    },
  },
  outlinedInput: {
    paddingRight: 0,
    paddingLeft: 0,
    paddingTop: spacing(2),
    paddingBottom: spacing(2),
  },
}));
